/**
 * Preflight-then-confirm dialog for deleting or restoring user accounts.
 * Opening it dry-runs the action against users/bulk/preflight/ and shows
 * what will happen, or why some or all of the accounts cannot be changed,
 * before the caller may confirm. Serves one account from the settings
 * Danger Zone and a selection of accounts from the user grid.
 *
 * Contains:
 * - UserLifecycleDialog (default export)
 */

import { Alert, Box, CircularProgress, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { usePreflightUserLifecycleMutation } from '@sophocles/features/entities/users';

import { lifecycleBlockerMessage } from './lifecycleMessages';
import { LIFECYCLE_ACTIONS, summarizePreflight } from './userLifecycle';

// [i18n key, English default] pairs for the consequence list, per action and
// per single/plural selection.
const EFFECTS = {
  [LIFECYCLE_ACTIONS.DELETE]: {
    one: [
      ['userLifecycle.effects.deleteSignsOut', 'Signs the user out everywhere and revokes their API access.'],
      [
        'userLifecycle.effects.deleteInvalidatesPassword',
        'Invalidates the current password and disables single sign-on.',
      ],
      ['userLifecycle.effects.deleteFreesEmail', 'Releases {{email}} so a new account can use it.'],
      ['userLifecycle.effects.deleteKeepsHistory', 'Keeps the account on record; it can be restored later.'],
    ],
    many: [
      ['userLifecycle.effects.deleteSignsOutMany', 'Signs the users out everywhere and revokes their API access.'],
      [
        'userLifecycle.effects.deleteInvalidatesPasswordMany',
        'Invalidates their passwords and disables single sign-on.',
      ],
      ['userLifecycle.effects.deleteFreesEmailMany', 'Releases each email address so a new account can use it.'],
      ['userLifecycle.effects.deleteKeepsHistoryMany', 'Keeps the accounts on record; each can be restored later.'],
    ],
  },
  [LIFECYCLE_ACTIONS.RESTORE]: {
    one: [
      ['userLifecycle.effects.restoreReactivates', 'Reactivates the account as {{email}}.'],
      ['userLifecycle.effects.restoreSso', 'Re-enables single sign-on where the account was linked.'],
      ['userLifecycle.effects.restoreResetEmail', 'Emails the user a password-reset link with a temporary password.'],
    ],
    many: [
      [
        'userLifecycle.effects.restoreReactivatesMany',
        'Reactivates the accounts under their original email addresses.',
      ],
      ['userLifecycle.effects.restoreSsoMany', 'Re-enables single sign-on where the accounts were linked.'],
      [
        'userLifecycle.effects.restoreResetEmailMany',
        'Emails each user a password-reset link with a temporary password.',
      ],
    ],
  },
};

function PreflightBody({ action, email, requestedCount, summary, isLoading, isError }) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !summary) {
    return (
      <Typography color="error">
        {t('userLifecycle.preflightFailed', 'Could not check these accounts. Please try again.')}
      </Typography>
    );
  }
  const isPlural = requestedCount > 1;
  // An email only names a single account; a plural selection never shows one.
  const effects = EFFECTS[action][isPlural || !email ? 'many' : 'one'];
  const proceedCount = summary.proceedIds.length;
  const isPartial = summary.canProceed && proceedCount < requestedCount;
  return (
    <Box>
      {summary.blockers.map(blocker => (
        <Alert key={blocker.code} severity="warning" sx={{ mb: 2 }}>
          {lifecycleBlockerMessage(t, blocker.code, { count: blocker.ids.length || 1, isPlural })}
        </Alert>
      ))}
      {summary.canProceed ? (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {isPartial
              ? t('userLifecycle.summaryPartial', 'For the other {{count}} accounts, this action will:', {
                  count: proceedCount,
                })
              : t('userLifecycle.summary', 'This action will:')}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {effects.map(([key, fallback]) => (
              <Typography key={key} component="li" variant="body2">
                {t(key, fallback, { email })}
              </Typography>
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

function dialogTitle(t, action, count) {
  if (action === LIFECYCLE_ACTIONS.DELETE) {
    return t('userLifecycle.deleteTitle', {
      count,
      defaultValue_one: 'Delete this user?',
      defaultValue_other: 'Delete {{count}} users?',
    });
  }
  return t('userLifecycle.restoreTitle', {
    count,
    defaultValue_one: 'Restore this user?',
    defaultValue_other: 'Restore {{count}} users?',
  });
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {() => void} props.onConfirm — fires only after a passing preflight
 * @param {'delete'|'restore'} props.action
 * @param {number[]} props.userIds — the accounts this dialog acts on; ignored
 *   under `selectAll`
 * @param {boolean} [props.selectAll] — act on every row the grid's filters
 *   match instead of `userIds`
 * @param {object} [props.filters] — the grid's JSON:API filter params that
 *   define the select-all set
 * @param {number} [props.count] — the server total behind `selectAll`
 * @param {string | null | undefined} [props.email] — the single account's
 *   address, shown in the consequence list; omit for a selection
 * @param {boolean} [props.submitting] — the caller's mutation is in flight
 */
export default function UserLifecycleDialog({
  open,
  onClose,
  onConfirm,
  action,
  userIds,
  selectAll = false,
  filters,
  count = 0,
  email,
  submitting = false,
}) {
  const { t } = useTranslation();
  const [preflight, { data, isLoading, isUninitialized, isError, reset }] = usePreflightUserLifecycleMutation();
  const isDelete = action === LIFECYCLE_ACTIONS.DELETE;

  // `reset` is rebuilt by RTK Query on every trigger (its identity tracks the
  // in-flight promise), so listing it as an effect dependency re-fires the
  // preflight after each response — an unbounded request loop. Read it
  // through a ref instead so the effect only tracks what it should. The target
  // rides a ref too, keyed by its contents, so a caller passing fresh
  // arrays or filter objects each render does not re-run the preflight.
  const resetRef = useRef(reset);
  resetRef.current = reset;
  const targetRef = useRef({ userIds, selectAll, filters });
  targetRef.current = { userIds, selectAll, filters };
  const targetKey = selectAll ? `all:${JSON.stringify(filters ?? {})}` : userIds.join(',');

  useEffect(() => {
    if (!open) {
      resetRef.current();
      return;
    }
    const target = targetRef.current;
    if (!target.selectAll && target.userIds.length === 0) return;
    preflight({ action, ...target });
  }, [open, action, targetKey, preflight]);

  const requestedCount = selectAll ? count : userIds.length;

  const summary = data ? summarizePreflight(action, data) : null;
  // Uninitialized covers the render between opening and the effect firing.
  const isChecking = isLoading || isUninitialized;
  const confirmDisabled = submitting || isChecking || !summary?.canProceed;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle(t, action, requestedCount)}</DialogTitle>
      <DialogContent dividers>
        <PreflightBody
          action={action}
          email={email}
          requestedCount={requestedCount}
          summary={summary}
          isLoading={isChecking}
          isError={isError}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color={isDelete ? 'error' : 'success'}
          onClick={onConfirm}
          disabled={confirmDisabled}
        >
          {isDelete ? t('actions.delete') : t('actions.restore')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
